import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BranchTable from "./BranchTable";
import AddBranchModal from "./AddBranchModal";

import { fetchBranches, fetchBranchStats } from "../api/branchesApi";
import { UserSessionContext } from "../../../session/UserSessionContext";
import { useNavigation } from "../../../contexts/NavigationContext";
import useAddBranch from "../hooks/useAddBranch";
import useEditBranch from "../hooks/useEditBranch";

export default function BranchesPage() {
  const navigate = useNavigate();
  const { userSession } = useContext(UserSessionContext) || {};
  const { setSelectedBranch, selectedBranchId } = useNavigation();

  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const accessToken = userSession?.auth?.accessToken || sessionStorage.getItem("accessToken");
  const email = userSession?.user?.email || sessionStorage.getItem("email");
  const jobTitle = userSession?.user?.jobTitle || sessionStorage.getItem("jobTitle");
  const userId = userSession?.user?.id || sessionStorage.getItem("UserID") || "";
  const organizationId = userSession?.organization?.id || sessionStorage.getItem("organizationId") || "";
  const canEdit = jobTitle === "Organization Admin" || jobTitle === "Branch Admin";

  const loadBranches = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    if (!accessToken) {
      setErrorMessage("Session expired. Please log in again.");
      setIsLoading(false);
      navigate("/");
      return;
    }

    if (!email) {
      setErrorMessage("Unable to determine the signed-in user.");
      setIsLoading(false);
      return;
    }

    try {
      const branchResponse = await fetchBranches({
        accessToken,
        email,
        jobTitle,
        organizationId,
        userId,
      });

      const branchesList = branchResponse?.branches || [];
      const branchesWithStats = await Promise.all(
        branchesList.map(async (branch) => {
          try {
            const stats = await fetchBranchStats({
              accessToken,
              branchId: branch.id,
              userId,
            });
            return {
              ...branch,
              employeeCount: stats.employee_count || 0,
              customerCount: stats.customer_count || 0,
            };
          } catch {
            return { ...branch, employeeCount: 0, customerCount: 0 };
          }
        }),
      );

      setBranches(branchesWithStats);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
      setErrorMessage("Unable to load branches right now.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, email, jobTitle, userId, navigate]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const {
    showModal,
    openModal,
    closeModal,
    formData,
    errors,
    submitError,
    isSubmitting,
    handleChange,
    handleSubmit,
  } = useAddBranch({
    accessToken,
    userId,
    organizationId,
    onSuccess: loadBranches,
  });

  const {
    showModal: showEditModal,
    openModal: openEditModal,
    closeModal: closeEditModal,
    formData: editFormData,
    errors: editErrors,
    submitError: editSubmitError,
    isSubmitting: isEditSubmitting,
    handleChange: handleEditChange,
    handleSubmit: handleEditSubmit,
  } = useEditBranch({
    accessToken,
    userId,
    onSuccess: loadBranches,
  });

  const handleSelect = (branch) => {
    setSelectedBranch(branch);
    navigate("/customer-projects");
  };

  const handleEdit = (branch) => {
    openEditModal(branch);
  };

  const handleDelete = (branch) => {
    console.log("Delete branch:", branch.id, branch.branch_name);
  };

  if (isLoading) {
    return <p>Loading branches...</p>;
  }

  if (errorMessage) {
    return <p>{errorMessage}</p>;
  }

  return (
    <>
      <BranchTable
        branches={branches}
        activeBranchId={selectedBranchId}
        onSelect={handleSelect}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAddBranch={openModal}
        canEdit={canEdit}
      />
      <AddBranchModal
        open={showModal}
        onClose={closeModal}
        formData={formData}
        errors={errors}
        submitError={submitError}
        isSubmitting={isSubmitting}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
      <AddBranchModal
        mode="edit"
        open={showEditModal}
        onClose={closeEditModal}
        formData={editFormData}
        errors={editErrors}
        submitError={editSubmitError}
        isSubmitting={isEditSubmitting}
        handleChange={handleEditChange}
        handleSubmit={handleEditSubmit}
      />
    </>
  );
}
